import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_5a86fca4-b52a-477d-a2ba-6ba3a9509c91/artifacts/dxnk9k47_The%20Guy%20In%20SA%20Main%20Logo.png';

export const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="bg-[#0A0F1A] text-white" data-testid="footer">
      <div className="container-custom pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 pb-12 border-b border-white/10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-5">
              <img src={LOGO_URL} alt="The Guy in SA" className="h-10 w-auto" />
            </Link>
            <p className="text-slate-500 text-sm leading-relaxed mb-4">
              {t('footer_brand_desc')}
            </p>
            <p className="text-[#F59E0B] text-sm font-medium italic">
              {t('footer_tagline')}
            </p>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-5">{t('footer_company')}</h3>
            <ul className="space-y-3">
              {[
                { to: '/services', label: t('footer_services') },
                { to: '/products', label: t('nav_products') },
                { to: '/for-buyers', label: t('nav_for_buyers') },
                { to: '/for-suppliers', label: t('nav_for_suppliers') },
                { to: '/contact', label: t('footer_contact') },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-slate-500 hover:text-white transition-colors text-sm flex items-center gap-1 group">
                    {link.label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-5">{t('footer_legal')}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/privacy" className="text-slate-500 hover:text-white transition-colors text-sm">
                  {t('footer_privacy')}
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-slate-500 hover:text-white transition-colors text-sm">
                  {t('footer_terms')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Offices */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-5">{t('footer_offices')}</h3>
            
            {/* SA */}
            <div className="mb-5">
              <h4 className="text-[#F59E0B] text-xs font-bold uppercase tracking-wider mb-2">{t('footer_sa_office')}</h4>
              <ul className="space-y-1.5 text-sm text-slate-500">
                <li className="flex items-start gap-2">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-600" />
                  <span>Atrium on 5th, 9th Floor, Sandton, 2196</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 flex-shrink-0 text-slate-600" />
                  <a href="tel:+27783891391" className="hover:text-white transition-colors">+27 78 389 1391</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 flex-shrink-0 text-slate-600" />
                  <a href="mailto:sales@theguyinsa.co.za" className="hover:text-white transition-colors">sales@theguyinsa.co.za</a>
                </li>
              </ul>
            </div>

            {/* MZ */}
            <div>
              <h4 className="text-[#10B981] text-xs font-bold uppercase tracking-wider mb-2">{t('footer_mz_office')}</h4>
              <ul className="space-y-1.5 text-sm text-slate-500">
                <li className="flex items-start gap-2">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-600" />
                  <span>Av. 24 de Julho, n° 797, 1° andar, Maputo</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 flex-shrink-0 text-slate-600" />
                  <a href="tel:+258857270112" className="hover:text-white transition-colors">+258 857 270 112</a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-600 text-xs">
            &copy; {new Date().getFullYear()} The Guy in SA. {t('footer_rights')}
          </p>
          <div className="flex items-center gap-4">
            <a href="https://www.facebook.com/theguyinsaa" target="_blank" rel="noopener noreferrer"
              className="text-slate-600 hover:text-white transition-colors" data-testid="footer-facebook">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a href="https://www.instagram.com/theguyinsa" target="_blank" rel="noopener noreferrer"
              className="text-slate-600 hover:text-white transition-colors" data-testid="footer-instagram">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
